import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import {
  getDocSectionId,
  getDocsAssetFragment,
  isDocsFrameRouteMessage,
  isDocsFrameSectionMessage,
  isTrustedDocsFrameMessage,
  stripDocSectionSearchParam,
  stripDocsAssetFragment,
  withDocsSectionFragment,
} from "@/lib/portal/doc-section";
import {
  GLOFOX_DOCS_HOME_ROUTE,
  resolveDocsFrameSrc,
  withDocsBaseUrl,
} from "@/lib/portal/docs-urls";

/** Give up waiting for the incoming page and reveal it anyway. */
const SWAP_TIMEOUT_MS = 3000;

type SlotIndex = 0 | 1;

/**
 * Full-bleed iframe hosting Markdoc HTML while the address bar stays on
 * `/api-portal/#/glofox/…` (HashRouter).
 *
 * In-page sections use nested hash (`#/glofox/authentication#rate-limiting`)
 * or legacy `?section=` — both map to the Markdoc asset `#fragment`.
 * `resolveDocsFrameSrc` may also bake a default fragment into the asset URL
 * (e.g. `/glofox/error-handling` → `api-reference.html#error-handling`); that
 * wins only when the SPA location has no section of its own.
 *
 * Pages are double-buffered across two iframes. A navigating iframe goes
 * transparent until its new document paints (browsers do not paint-hold
 * iframes), which shows the portal background through it as a full-viewport
 * flash. So the next page loads in the offscreen slot and is revealed only once
 * it has painted, keeping the outgoing page on screen until then.
 *
 * Navigation is driven through `location.replace` rather than the `src` prop:
 * writing `src` on a live iframe pushes an entry onto the parent's session
 * history, which desynchronizes Back from the HashRouter route.
 */
export default function DocsFramePage() {
  const { pathname, search, hash } = useLocation();
  const navigate = useNavigate();

  const locationSectionId = getDocSectionId(search, hash);
  const resolvedSrc = resolveDocsFrameSrc(pathname);
  const resolvedOrFallback = resolvedSrc ?? withDocsBaseUrl("/glofox/docs/");
  // Slot identity is path-only; fragments are applied via `sectionId`.
  const baseSrc = stripDocsAssetFragment(resolvedOrFallback);
  const sectionId =
    locationSectionId ?? getDocsAssetFragment(resolvedOrFallback);

  const frameRefs = useRef<(HTMLIFrameElement | null)[]>([null, null]);
  const [visibleSlot, setVisibleSlot] = useState<SlotIndex>(0);
  // Which page each slot holds, and which one is currently loading offscreen.
  // Tracking this per slot keeps section updates off the outgoing page: during a
  // swap the visible slot still holds the previous page.
  const slotBaseSrcRef = useRef<(string | null)[]>([baseSrc, null]);
  const pendingBaseSrcRef = useRef<string | null>(null);
  // Slot 0 renders the first page through `src`: the initial load of a fresh
  // iframe does not create a history entry, unlike later `src` writes.
  const [initialSrc] = useState(() => withDocsSectionFragment(baseSrc, sectionId));

  // Stale bookmark or broken link: keep the address bar in sync with the docs
  // home we fall back to, instead of showing it under an unknown route.
  useEffect(() => {
    if (resolvedSrc || pathname === GLOFOX_DOCS_HOME_ROUTE) return;
    navigate(GLOFOX_DOCS_HOME_ROUTE, { replace: true });
  }, [navigate, pathname, resolvedSrc]);

  // Canonicalize legacy `?section=` bookmarks to nested hash.
  useEffect(() => {
    const params = new URLSearchParams(
      search.startsWith("?") ? search.slice(1) : search,
    );
    if (!params.has("section") || !sectionId) {
      return;
    }
    navigate(
      {
        pathname,
        search: stripDocSectionSearchParam(search),
        hash: `#${sectionId}`,
      },
      { replace: true },
    );
  }, [navigate, pathname, search, sectionId]);

  // Load the new page into the offscreen slot, then reveal it once painted.
  useEffect(() => {
    if (slotBaseSrcRef.current[visibleSlot] === baseSrc) return;
    if (pendingBaseSrcRef.current === baseSrc) return;

    const target: SlotIndex = visibleSlot === 0 ? 1 : 0;
    const frame = frameRefs.current[target];
    if (!frame) return;

    // The offscreen slot may already hold the destination — going back to the
    // page it was swapped away from. Only the fragment can differ then, and a
    // hash-only `location.replace` is a same-document navigation, so no new
    // Document would ever arrive for the poll below to detect.
    if (slotBaseSrcRef.current[target] === baseSrc && revealInPlace(frame, sectionId)) {
      setVisibleSlot(target);
      return;
    }

    pendingBaseSrcRef.current = baseSrc;
    const nextUrl = withDocsSectionFragment(baseSrc, sectionId);
    const previousDocument = safeDocument(frame);

    try {
      const win = frame.contentWindow;
      if (!win) throw new Error("no contentWindow");
      win.location.replace(nextUrl);
    } catch {
      // Cross-origin docs host: fall back to the attribute, accepting the
      // extra history entry over not navigating at all.
      frame.src = nextUrl;
    }

    let settled = false;
    let raf = 0;
    let timer = 0;

    const reveal = () => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      window.cancelAnimationFrame(raf);
      slotBaseSrcRef.current[target] = baseSrc;
      pendingBaseSrcRef.current = null;
      setVisibleSlot(target);
    };

    // Reveal once the incoming document has parsed and its stylesheet has
    // applied — waiting for `load` would also wait on page images, while
    // revealing any earlier can show unstyled content. Comparing document
    // identity avoids matching the outgoing document, which stays `complete`
    // until the replacement commits.
    const poll = () => {
      const current = safeDocument(frame);
      raf = window.requestAnimationFrame(
        current && current !== previousDocument && isPainted(current) ? reveal : poll,
      );
    };

    timer = window.setTimeout(reveal, SWAP_TIMEOUT_MS);
    raf = window.requestAnimationFrame(poll);

    return () => {
      settled = true;
      window.clearTimeout(timer);
      window.cancelAnimationFrame(raf);
      // Abandoned before it landed, so the same page must be loadable again.
      if (pendingBaseSrcRef.current === baseSrc) pendingBaseSrcRef.current = null;
    };
    // `visibleSlot` is read to pick the offscreen slot but must not retrigger
    // the swap when it lands.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseSrc, sectionId]);

  // When only the section changes on the same docs page, move the visible
  // iframe's hash in place instead of reloading it.
  useEffect(() => {
    if (slotBaseSrcRef.current[visibleSlot] !== baseSrc) return;
    const frame = frameRefs.current[visibleSlot];
    if (!frame) return;

    applySectionHash(frame, sectionId);
  }, [baseSrc, sectionId, visibleSlot]);

  // Markdoc iframe reports TOC / in-page anchor navigation.
  useEffect(() => {
    function onMessage(event: MessageEvent) {
      const frames = frameRefs.current.filter(
        (frame): frame is HTMLIFrameElement => frame !== null,
      );
      if (!isTrustedDocsFrameMessage(event, frames)) return;

      if (isDocsFrameRouteMessage(event.data)) {
        const nextPathname = event.data.pathname;
        const nextSearch = event.data.search ?? "";
        const nextHash = event.data.hash ?? "";
        if (
          pathname === nextPathname &&
          search === nextSearch &&
          hash === nextHash
        ) {
          return;
        }
        navigate({
          pathname: nextPathname,
          search: nextSearch,
          hash: nextHash,
        });
        return;
      }

      if (!isDocsFrameSectionMessage(event.data)) return;

      const nextId = event.data.id?.trim() || null;
      const current = getDocSectionId(search, hash);
      if (current === nextId) return;

      navigate(
        {
          pathname,
          search: stripDocSectionSearchParam(search),
          hash: nextId ? `#${nextId}` : "",
        },
        { replace: true },
      );
    }

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [hash, navigate, pathname, search]);

  return (
    <div className="gf-docs-frame-stack">
      {([0, 1] as SlotIndex[]).map((slot) => {
        const visible = slot === visibleSlot;
        return (
          <iframe
            key={slot}
            ref={(node) => {
              frameRefs.current[slot] = node;
            }}
            title="Documentation"
            {...(slot === 0 ? { src: initialSrc } : {})}
            {...(visible ? { "data-testid": "gf-docs-frame" } : {})}
            aria-hidden={visible ? undefined : true}
            className="gf-docs-frame"
            style={{
              visibility: visible ? "visible" : "hidden",
              zIndex: visible ? 1 : 0,
            }}
          />
        );
      })}
    </div>
  );
}

/**
 * Move an already-loaded frame's fragment to `sectionId`. Returns false when
 * the frame is cross-origin and cannot be steered from here.
 */
function applySectionHash(
  frame: HTMLIFrameElement,
  sectionId: string | null,
): boolean {
  try {
    const win = frame.contentWindow;
    if (!win) return false;
    const desired = sectionId ? `#${sectionId}` : "";
    if (win.location.hash === desired) return true;
    if (desired) {
      win.location.hash = desired;
    } else {
      win.history.replaceState(
        null,
        "",
        `${win.location.pathname}${win.location.search}`,
      );
    }
    return true;
  } catch {
    return false;
  }
}

/**
 * Show a buffered page without reloading it. Scroll is reset for a
 * section-less route so a revisited page opens at the top, the way a fresh
 * load would.
 */
function revealInPlace(
  frame: HTMLIFrameElement,
  sectionId: string | null,
): boolean {
  if (!applySectionHash(frame, sectionId)) return false;
  if (!sectionId) {
    try {
      frame.contentWindow?.scrollTo(0, 0);
    } catch {
      // cross-origin — ignore
    }
  }
  return true;
}

function safeDocument(frame: HTMLIFrameElement): Document | null {
  try {
    return frame.contentDocument;
  } catch {
    return null;
  }
}

/** Parsed, with any linked stylesheet resolved (`sheet` stays null until then). */
function isPainted(doc: Document): boolean {
  if (doc.readyState === "loading") return false;
  const link = doc.querySelector<HTMLLinkElement>('link[rel="stylesheet"]');
  return !link || !!link.sheet;
}
